import { redraw } from './profilePhoto';

/**
 * A nursery's logo, shrunk in the browser before upload as a profile photo is, but kept whole.
 *
 * A logo is not cropped: a wide wordmark cut to its centre square is no longer the nursery's logo. It is
 * scaled to fit inside a square (`media.logoMaximumEdge` from `useLimits`) and re-encoded as PNG, so a
 * transparent background stays transparent on the dark theme rather than becoming a white box.
 */

/** The size an image is drawn at to fit inside a `maxSide` square. A logo is never enlarged. */
export function fitWithin(width: number, height: number, maxSide: number): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= 0) return { width: 0, height: 0 };
  const scale = Math.min(1, maxSide / longest);
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/**
 * Decodes an image file and re-encodes it as a PNG no larger than `maxSide` on its longest side.
 *
 * @throws UnreadablePhotoError when the browser cannot decode the file.
 */
export function toLogo(file: Blob, maxSide: number): Promise<Blob> {
  return redraw(file, (image) => {
    const size = fitWithin(image.naturalWidth, image.naturalHeight, maxSide);
    return { ...size, draw: (context) => context.drawImage(image, 0, 0, size.width, size.height) };
  }, 'image/png');
}
